import { ActionIcon, Badge, Button, Group, Menu, Modal, Paper, Stack, Switch, Table, Text, TextInput, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { IconArchive, IconArchiveOff, IconDots, IconEdit, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClients, useDeleteClient, useSaveClient, type Client } from '@/api/catalog';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { PageHeader } from '@/components/PageHeader';
import { useApiError } from '@/hooks/useApiError';

/** Clients verwalten (PRJ-09). */
export function ClientsPage() {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const { data: clients = [] } = useClients(ctx.workspaceId);
  const save = useSaveClient(ctx.workspaceId);
  const remove = useDeleteClient(ctx.workspaceId);
  const onError = useApiError();
  const [search, setSearch] = useState('');
  const [archived, setArchived] = useState(false);
  const [editing, setEditing] = useState<Client | 'new' | null>(null);
  const canCreate = ctx.isAdmin || ctx.workspace.settings.permissions.createClients === 'EVERYONE';
  const visible = clients.filter(
    (c) => c.archived === archived && c.name.toLowerCase().includes(search.trim().toLowerCase()),
  );

  return (
    <>
      <PageHeader title={t('nav.items.clients')}>
        {canCreate && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing('new')}>
            {t('clients.create')}
          </Button>
        )}
      </PageHeader>
      <Group mb="md">
        <TextInput leftSection={<IconSearch size={14} />} placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.currentTarget.value)} />
        <Switch label={t('projects.showArchived')} checked={archived} onChange={(e) => setArchived(e.currentTarget.checked)} />
      </Group>
      <Paper withBorder>
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('clients.name')}</Table.Th>
              <Table.Th>{t('clients.email')}</Table.Th>
              <Table.Th>{t('clients.currency')}</Table.Th>
              <Table.Th w={50} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {visible.map((c) => (
              <Table.Tr key={c.id}>
                <Table.Td>
                  <Group gap="xs">
                    {c.name}
                    {c.archived && <Badge size="xs" color="gray">{t('common.archived')}</Badge>}
                  </Group>
                </Table.Td>
                <Table.Td>{c.email ?? '—'}</Table.Td>
                <Table.Td>{c.currency ?? ctx.workspace.currency}</Table.Td>
                <Table.Td>
                  {ctx.isAdmin && (
                    <Menu position="bottom-end" withinPortal>
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray" aria-label={t('common.more')}>
                          <IconDots size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => setEditing(c)}>
                          {t('common.edit')}
                        </Menu.Item>
                        <Menu.Item
                          leftSection={c.archived ? <IconArchiveOff size={14} /> : <IconArchive size={14} />}
                          onClick={() => save.mutate({ id: c.id, archived: !c.archived }, { onError })}
                        >
                          {c.archived ? t('common.restore') : t('common.archive')}
                        </Menu.Item>
                        <Menu.Item
                          color="red"
                          leftSection={<IconTrash size={14} />}
                          onClick={() =>
                            modals.openConfirmModal({
                              title: t('clients.deleteTitle'),
                              children: <Text size="sm">{t('clients.deleteText', { name: c.name })}</Text>,
                              labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
                              confirmProps: { color: 'red' },
                              onConfirm: () => remove.mutate(c.id, { onError }),
                            })
                          }
                        >
                          {t('common.delete')}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
            {visible.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text c="dimmed" ta="center" py="md">
                    {t('clients.empty')}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Paper>
      <ClientModal client={editing} onClose={() => setEditing(null)} />
    </>
  );
}

function ClientModal({ client, onClose }: { client: Client | 'new' | null; onClose: () => void }) {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const save = useSaveClient(ctx.workspaceId);
  const onError = useApiError();
  const existing = client && client !== 'new' ? client : undefined;
  const form = useForm({
    initialValues: { name: '', email: '', address: '', note: '', currency: '' },
    validate: { name: (v) => (v.trim() ? null : t('common.required')) },
  });
  useEffect(() => {
    if (client) {
      form.setValues({
        name: existing?.name ?? '',
        email: existing?.email ?? '',
        address: existing?.address ?? '',
        note: existing?.note ?? '',
        currency: existing?.currency ?? '',
      });
    }
  }, [client]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal opened={!!client} onClose={onClose} title={existing ? t('clients.edit') : t('clients.create')}>
      <form onSubmit={form.onSubmit((v) => save.mutate({ id: existing?.id, ...v }, { onSuccess: onClose, onError }))}>
        <Stack>
          <TextInput label={t('clients.name')} data-autofocus {...form.getInputProps('name')} />
          <TextInput label={t('clients.email')} type="email" {...form.getInputProps('email')} />
          <Textarea label={t('clients.address')} autosize minRows={2} {...form.getInputProps('address')} />
          <TextInput label={t('clients.currency')} placeholder={ctx.workspace.currency} maxLength={3} {...form.getInputProps('currency')} />
          <Textarea label={t('clients.note')} autosize minRows={2} {...form.getInputProps('note')} />
          <Button type="submit" loading={save.isPending}>
            {t('common.save')}
          </Button>
        </Stack>
      </form>
    </Modal>
  );
}
